import { Component, OnInit } from '@angular/core';
import { validateMediaFile, getVideoDuration } from '../media-validation';
import { MatDialog } from '@angular/material/dialog';
import { ConfirmDialogComponent } from '../../support/confirm-dialog.component';
import { FormBuilder, FormGroup } from '@angular/forms';
import { MediaService } from '../media.service';
import { ImageService } from '../media.firebase.service';
import { AuthService } from '../../auth/auth.service';
import { Image } from '../../models/image';
import { Video } from '../../models/video';
import { MatSnackBar } from '@angular/material/snack-bar';
import { firstValueFrom } from 'rxjs';

@Component({
  selector: 'app-content-manager',
  templateUrl: './content-manager.component.html',
  styleUrls: ['./content-manager.component.css']
})
export class ContentManagerComponent implements OnInit {
  uploadForm: FormGroup;
  images: Image[] = [];
  videos: Video[] = [];
  selectedFile: File | null = null;
  isUploading = false;
  uploadType: 'image' | 'video' = 'image';
  imageLoading: boolean[] = [];
  videoLoading: boolean[] = [];
  selectedFileError: string = '';

  constructor(
    private fb: FormBuilder,
    private mediaService: MediaService,
    private imageService: ImageService,
    private authService: AuthService,
    private snackBar: MatSnackBar,
    private dialog: MatDialog
  ) {
    this.uploadForm = this.fb.group({
      file: [null],
      description: ['']
    });
  }

  ngOnInit(): void {
    const userId = this.authService.getCurrentUid();
    if (userId) {
      this.mediaService.getUserImages(userId).subscribe(images => {
        this.images = images;
        this.imageLoading = this.images.map(() => true);
      });
      this.mediaService.getUserVideos(userId).subscribe(videos => {
        this.videos = videos;
        this.videoLoading = this.videos.map(() => true);
      });
    }
  }

  onImageLoad(idx: number) {
    this.imageLoading[idx] = false;
  }

  onImageError(idx: number) {
    this.imageLoading[idx] = false;
  }

  onVideoLoad(idx: number) {
    this.videoLoading[idx] = false;
  }

  onVideoError(idx: number) {
    this.videoLoading[idx] = false;
  }

  async onFileChange(event: any) {
    this.selectedFileError = '';
    if (event.target.files.length > 0) {
      const file = event.target.files[0];
      let validationResult;
      if (this.uploadType === 'video') {
        try {
          const duration = await getVideoDuration(file);
          validationResult = validateMediaFile(file, 'video', duration);
        } catch (err) {
          this.selectedFile = null;
          this.uploadForm.patchValue({ file: null });
          this.selectedFileError = 'Could not read video duration.';
          this.snackBar.open('Could not read video duration.', 'Close', { duration: 3000, panelClass: ['error-snackbar'] });
          return;
        }
      } else {
        validationResult = validateMediaFile(file, 'image');
      }
      if (!validationResult.valid) {
        this.selectedFile = null;
        this.uploadForm.patchValue({ file: null });
        this.selectedFileError = validationResult.error || 'Invalid file.';
        this.snackBar.open(this.selectedFileError, 'Close', { duration: 4000, panelClass: ['error-snackbar'] });
        return;
      }
      this.selectedFile = file;
      this.selectedFileError = '';
    }
  }

  setUploadType(type: 'image' | 'video') {
    this.uploadType = type;
    this.selectedFile = null;
    this.uploadForm.reset();
  }

  triggerFileInput(type: 'image' | 'video') {
    this.uploadType = type;
    const inputId = type === 'image' ? 'image-upload' : 'video-upload';
    const fileInput = document.getElementById(inputId) as HTMLInputElement;
    if (fileInput) {
      fileInput.click();
    }
  }

  async uploadMedia() {
    if (!this.selectedFile) return;
    this.isUploading = true;
    const userId = this.authService.getCurrentUid();
    const description = this.uploadForm.value.description;
    let firebaseUrl = '';
    try {
      if (this.uploadType === 'image') {
        firebaseUrl = await this.imageService.uploadImage(this.selectedFile, `model_images/${Date.now()}_${this.selectedFile.name}`);
        await firstValueFrom(this.mediaService.uploadImage({ userId: userId!, firebaseUrl, description }));
        const images = await firstValueFrom(this.mediaService.getUserImages(userId!));
        this.images = images;
        this.imageLoading = this.images.map(() => true);
      } else {
        firebaseUrl = await this.imageService.uploadVideo(this.selectedFile, `model_videos/${Date.now()}_${this.selectedFile.name}`);
        await firstValueFrom(this.mediaService.uploadVideo({ userId: userId!, firebaseUrl, description }));
        const videos = await firstValueFrom(this.mediaService.getUserVideos(userId!));
        this.videos = videos;
        this.videoLoading = this.videos.map(() => true);
      }
        this.snackBar.open('Upload successful!', 'Close', {
            duration: 2000,
            panelClass: ['success-snackbar']
        });
      this.uploadForm.reset();
      this.selectedFile = null;
    } catch (err) {
      console.error('Upload failed:', err);
      const errorMessage = (err && typeof err === 'object' && 'message' in err) ? (err as any).message : 'Unknown error';
      this.snackBar.open('Upload failed: ' + errorMessage, 'Close', {
        duration: 4000,
        panelClass: ['error-snackbar']
      });
    }
    this.isUploading = false;
  }
  async confirmDelete(type: 'image' | 'video', item: any, idx: number) {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '350px',
      data: {
        title: 'Delete Confirmation',
        message: `Are you sure you want to delete this ${type}? This action cannot be undone.`
      }
    });
    const confirmed = await firstValueFrom(dialogRef.afterClosed());
    if (confirmed) {
      try {
        // Remove from Firebase
        await this.imageService.deleteFile(item.firebaseUrl);
        // Remove from backend
        if (type === 'image') {
          await firstValueFrom(this.mediaService.deleteImage(item._id || item.id));
          this.images.splice(idx, 1);
          this.imageLoading.splice(idx, 1);
        } else {
          await firstValueFrom(this.mediaService.deleteVideo(item._id || item.id));
          this.videos.splice(idx, 1);
          this.videoLoading.splice(idx, 1);
        }
        this.snackBar.open(`${type.charAt(0).toUpperCase() + type.slice(1)} deleted!`, 'Close', {
          duration: 2000,
          panelClass: ['success-snackbar']
        });
      } catch (err) {
        this.snackBar.open(`Failed to delete ${type}.`, 'Close', {
          duration: 4000,
          panelClass: ['error-snackbar']
        });
      }
    }
  }
}
